<template>
  <LoaderAnimation />
  <AgeVerification v-if="showAgeModal" @verified="onAgeVerified" />
  <NavbarComponent
    v-if="!shouldHideNavbar && !showAgeModal && !globalPageLoading"
  />
  <BlogNavigation
    v-if="showBlogNavigation && !showAgeModal && !globalPageLoading"
    class="mt-5"
  />
  <router-view v-if="!showAgeModal" />
  <FooterComponent v-if="!shouldHideNavbar && !showAgeModal && !globalPageLoading" />
    <!-- WhatsApp Floating Button -->
  <a :href="whatsappUrl" 
     class="whatsapp-float text-decoration-none" 
     target="_blank" 
     rel="noopener noreferrer"
     v-if="!shouldHideNavbar && !showAgeModal && !globalPageLoading">
    <span class="whatsapp-tooltip">¿Necesitas ayuda?</span>
    <FaIcon name="whatsapp" brand class="whatsapp-icon" />
  </a>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import LoaderAnimation from '@/components/Loader.vue'
import BlogNavigation from '@/components/BlogNavigation.vue'
import AgeVerification from '@/components/AgeVerification.vue'
import { globalPageLoading } from '@/utils/routeLoadingState'
import { isAuthenticated } from '@/services/auth.js'
import { useMaintenanceMonitor } from '@/utils/maintenanceMonitor'

const route = useRoute()
const isAdmin = ref(localStorage.getItem('isAdmin') === 'true')

// Estado para mostrar el modal de verificación de edad
const showAgeModal = ref(false)

// Función para chequear si debe mostrarse el modal
const checkAgeVerification = () => {
  // Solo mostrar si NO está logueado y no verificó edad
  if (!isAuthenticated() && localStorage.getItem('ageVerified') !== 'true') {
    showAgeModal.value = true
  } else {
    showAgeModal.value = false
  }
}

// Nueva función para manejar el evento del modal
function onAgeVerified() {
  showAgeModal.value = false
  localStorage.setItem('ageVerified', 'true')
}

// WhatsApp configuration
const whatsappNumber = '+5491158162028'
const whatsappMessage = 'Hola%20Jamrock%20Grow%20Shop,%20quisiera%20hacer%20una%20consulta'
const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`

// Check if current route is an admin route or account page when user is admin
const shouldHideNavbar = computed(() => {
  // Ocultar navbar/footer en rutas de blog para todos
  if (route.path.startsWith('/blog')) {
    return true
  }
  // Ocultar navbar/footer en cualquier ruta de /configuracion (panel usuario o admin)
  if (route.path.startsWith('/configuracion')) {
    return true
  }
  if (route.path.startsWith('/mantenimiento')) {
    return true
  }
  return false
})

// Show blog navigation on blog routes
const showBlogNavigation = computed(() => {
  return route.path.startsWith('/blog')
})

// Update isAdmin value when localStorage changes
const updateAdminStatus = () => {
  isAdmin.value = localStorage.getItem('isAdmin') === 'true'
}

// Watch for route changes to recheck conditions
watch(() => route.path, () => {
  updateAdminStatus()
  checkAgeVerification()
})

onMounted(() => {
  document.getElementById('jamrock-static-head')?.remove()

  // Initial check
  updateAdminStatus()
  checkAgeVerification()
  
  // Inicializar verificación de mantenimiento con TTL más largo (5 minutos)
  // para reducir consultas al servidor
  useMaintenanceMonitor(5) // Check every 5 minutes (aumentado de 2 a 5 minutos)
  
  // Listen for storage events (in case user logs in/out in another tab)
  window.addEventListener('storage', () => {
    updateAdminStatus()
    checkAgeVerification()
  })
})
</script>

<style>
  /* Base primero; el resto de este bloque sobrescribe Bootstrap */
  @import '~bootstrap/dist/css/bootstrap.min.css';
  @import '@/assets/css/variables.css';
@font-face {
    font-family: 'ClashGrotesk-Variable';
    src: url('~@/assets/fonts/ClashGrotesk-Variable.woff2') format('woff2'),
         url('~@/assets/fonts/ClashGrotesk-Variable.ttf') format('truetype');
    font-weight: 100 900;
    font-style: normal;
    font-display: swap;
    }
@font-face {
    font-family: 'Good Brush';
    src: url('~@/assets/fonts/Good-Brush.woff2') format('woff2'),
         url('~@/assets/fonts/Good-Brush.ttf') format('truetype');
    font-style: normal;
    font-display: swap;
    }

.font-good-brush {
    font-family: 'Good Brush', cursive !important;
}
* {
margin: 0;
padding: 0;
box-sizing: border-box;
font-family: 'ClashGrotesk-Variable', sans-serif;
scroll-behavior: smooth;
}

body {
background-color: var(--bg-color);
color: var(--black-color-2);
}

.container{
max-width: 1400px;
}
.bg-dark{
background-color: var(--black-color-3) !important;
}
.border-primary{
border-color: var(--primary-color) !important;
}	

.text-muted{
  color: var(--white-color-2) !important;
}

.display-2, .display-3, .display-4, .display-5, .text-white{
font-weight: 800;
color: var(--white-color-2);
}
.text-gradient-primary {  background: linear-gradient(160deg, #000000, var(--black-color-2));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: waveEffect 2s ease-in-out infinite;
  background-size: 200% 200%;
}

section{
min-height: 50vh;
position: relative;
overflow: hidden;
background-color: var(--bg-color);
}

.h5{
font-size: 1.1rem;
font-weight: 500;
color: var(--black-color);
}
.fs-6{
font-size: .8rem;
}
.fs-7 {
  font-size: 0.875rem !important;
}
.btn{
font-size: .8rem;
}
.btn:hover{
  opacity: .6;
  color: var(--black-color);
}

.btn i:hover{
  color: var(--black-color-2);
}
.hover-opacity{
transition: 0.3s ease;
}
.hover-opacity:hover{
opacity: 0.8;
}
/* badges */
.status-badge {
display: inline-flex;
padding: 0.4rem 0.8rem;
border-radius: 2rem;
font-size: 0.875rem;
font-weight: 500;
white-space: nowrap;
}
.btn-outline-primary{
background-color: transparent;
color: var(--primary-color);
border: 1px solid var(--primary-color);
cursor: pointer;
transition: 0.3s ease;
}
.btn-outline-primary.active{
background-color: var(--primary-color);
color: var(--black-color);
border: 1px solid var(--primary-color);
}
.btn-outline-primary:hover{
background-color: var(--primary-color);
color: var(--black-color);
border: 1px solid var(--primary-color);
}
.btn-outline-primary:hover i{
color: var(--black-color);
}
.text-primary{
color: var(--primary-color) !important;
}
.bg-primary{
background-color: var(--primary-color) !important;
}
.text-dark{
color: var(--black-color);
}
.btn{
padding: 0.5rem 1rem;
border-radius: 10px;
}
.btn-primary{
background-color: var(--primary-color) !important;
color: #fff !important;
border: 1px solid var(--primary-color) !important;
padding: .5rem 1.3rem;
}
button,a{
font-weight: 400;
font-size: 1.1rem;
}
h1, h2, h3, h4, h5, h6 {
font-weight: 600;
color: var(--black-color);
}
h1 {
font-size: 2rem;
}
h2 {
font-size: 1.7rem;
}
h3 {
font-size: 2rem;
font-weight: 300;
letter-spacing: 1px;
}
h4 {
font-size: 1.5rem;
font-weight: 300;
}
h5 {
font-size: .75rem;
font-weight: 300;
}
p{
font-size: 1rem;
color: var(--black-color-2);
font-weight: 300;
}
b{
font-weight: 700;
}

@media (max-width: 525px) {
  .display-2{
    font-size: 3.5rem !important;
  }
}
@media (max-width: 400px) {
  .display-2{
    font-size: 3rem !important;
  }
}

/* WhatsApp Floating Button Styles */
.whatsapp-float {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #25d366;
  color: #ffffff;
  border-radius: 50px;
  text-align: center;
  font-size: 30px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  transition: all 0.3s;
}
.whatsapp-float:hover {
  background-color: #20ba5a;
  color: white;
}

.whatsapp-icon.inline-fa {
  font-size: 30px;
}

svg.inline-fa.fa-2x { font-size: 2em; }
svg.inline-fa.fa-3x { font-size: 3em; }


.whatsapp-tooltip {
  position: absolute;
  right: 60px;
  background-color: #333;
  color: white;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.whatsapp-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent #333;
}

.whatsapp-float:hover .whatsapp-tooltip {
  visibility: visible;
  opacity: 1;
}

/* Make button responsive */
@media screen and (max-width: 768px) {
  .whatsapp-float {
    width: 50px;
    height: 50px;
    font-size: 25px;
    bottom: 15px;
    right: 15px;
  }
  
  .whatsapp-tooltip {
    display: none; /* Hide tooltip on mobile */
  }
}
</style>
